import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { applyRouteSeo } from "@/lib/routeSeo";
import { startEngagementTracking, trackPageView } from "@/lib/siteTracking";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import FooterCTA from "@/components/FooterCTA";
import Index from "./pages/Index.tsx";
import LicenciaCdlRemolquesPage from "./pages/es/LicenciaCdlRemolquesPage.tsx";
import GvwrCapacidadDeCargaPage from "./pages/es/GvwrCapacidadDeCargaPage.tsx";
import RemolquesDeVolteoPage from "./pages/es/RemolquesDeVolteoPage.tsx";
import RemolqueVolteo14kVs16kPage from "./pages/es/RemolqueVolteo14kVs16kPage.tsx";
import MantenimientoDeRemolquesPage from "./pages/es/MantenimientoDeRemolquesPage.tsx";
import CostosDeTenerUnRemolquePage from "./pages/es/CostosDeTenerUnRemolquePage.tsx";
import CookieConsent from "./components/CookieConsent.tsx";

// Lazy-load all non-homepage routes to reduce initial JS bundle size.
const DumpTrailersPage = lazy(() => import("./pages/DumpTrailersPage.tsx"));
const RollOffTrailersPage = lazy(() => import("./pages/RollOffTrailersPage.tsx"));
const CarHaulersPage = lazy(() => import("./pages/CarHaulersPage.tsx"));
const FlatbedTrailersPage = lazy(() => import("./pages/FlatbedTrailersPage.tsx"));
const LowboyTrailersPage = lazy(() => import("./pages/LowboyTrailersPage.tsx"));
const TiltTrailersPage = lazy(() => import("./pages/TiltTrailersPage.tsx"));
const GravityTiltTrailersPage = lazy(() => import("./pages/GravityTiltTrailersPage.tsx"));
const PowerTiltTrailersPage = lazy(() => import("./pages/PowerTiltTrailersPage.tsx"));

const FlatbedBuilderPage = lazy(() => import("./pages/FlatbedBuilderPage.tsx"));
const RollOffBuilderPage = lazy(() => import("./pages/RollOffBuilderPage.tsx"));
const GravityTiltBuilderPage = lazy(() => import("./pages/GravityTiltBuilderPage.tsx"));
const PowerTiltBuilderPage = lazy(() => import("./pages/PowerTiltBuilderPage.tsx"));
const DumpTrailerBuilderPage = lazy(() => import("./pages/DumpTrailerBuilderPage.tsx"));
const CarHaulerBuilderPage = lazy(() => import("./pages/CarHaulerBuilderPage.tsx"));
const EquipmentTrailerBuilderPage = lazy(() => import("./pages/EquipmentTrailerBuilderPage.tsx"));
const DumpTrailerBuyersGuide = lazy(() => import("./pages/DumpTrailerBuyersGuide.tsx"));
const TrailerFinancingBuyersGuide = lazy(() => import("./pages/TrailerFinancingBuyersGuide.tsx"));
const DumpTrailer14kVs16kPage = lazy(() => import("./pages/DumpTrailer14kVs16kPage.tsx"));
const BlogPage = lazy(() => import("./pages/BlogPage.tsx"));
const DumpVsRollOff = lazy(() => import("./pages/blog/DumpVsRollOff.tsx"));
const GravityVsPowerTilt = lazy(() => import("./pages/blog/GravityVsPowerTilt.tsx"));
const BestDumpTrailerContractors = lazy(() => import("./pages/blog/BestDumpTrailerContractors.tsx"));
const WhatIsALowboyTrailer = lazy(() => import("./pages/blog/WhatIsALowboyTrailer.tsx"));
const LowboyTrailerDeckWidth = lazy(() => import("./pages/blog/LowboyTrailerDeckWidth.tsx"));
const LowboyTrailerAxleCapacity = lazy(() => import("./pages/blog/LowboyTrailerAxleCapacity.tsx"));
const LowboyTrailerRailsVsOpenDeck = lazy(() => import("./pages/blog/LowboyTrailerRailsVsOpenDeck.tsx"));
const GooseneckVsBumperPullLowboy = lazy(() => import("./pages/blog/GooseneckVsBumperPullLowboy.tsx"));
const BestTrailerForSkidSteer = lazy(() => import("./pages/blog/BestTrailerForSkidSteer.tsx"));
const BestTrailerForMiniExcavator = lazy(() => import("./pages/blog/BestTrailerForMiniExcavator.tsx"));
const LowboyGuidesHub = lazy(() => import("./pages/blog/LowboyGuidesHub.tsx"));
const LowboyVsEquipment = lazy(() => import("./pages/blog/LowboyVsEquipment.tsx"));
const LowboyTrailerSize = lazy(() => import("./pages/blog/LowboyTrailerSize.tsx"));
const LowboyWeightCapacity = lazy(() => import("./pages/blog/LowboyWeightCapacity.tsx"));
const LowboyRampsVsTilt = lazy(() => import("./pages/blog/LowboyRampsVsTilt.tsx"));
const RollOffTrailersExplained = lazy(() => import("./pages/blog/RollOffTrailersExplained.tsx"));
const RollOffRailWidth = lazy(() => import("./pages/blog/RollOffRailWidth.tsx"));
const RollOffCDL = lazy(() => import("./pages/blog/RollOffCDL.tsx"));
const RollOffDerating = lazy(() => import("./pages/blog/RollOffDerating.tsx"));
const RollOffSize = lazy(() => import("./pages/blog/RollOffSize.tsx"));
const RollOffResidentialVsCommercial = lazy(() => import("./pages/blog/RollOffResidentialVsCommercial.tsx"));
const TrailerGuidesHubPage = lazy(() => import("./pages/education/TrailerGuidesHubPage.tsx"));
const EducationHubPage = lazy(() => import("./pages/education/EducationHubPage.tsx"));
const AboutPage = lazy(() => import("./pages/About.tsx"));
const FinancingPage = lazy(() => import("./pages/FinancingPage.tsx"));
const PodcastPage = lazy(() => import("./pages/PodcastPage.tsx"));
const DynamicBlogArticle = lazy(() => import("./pages/blog/DynamicBlogArticle.tsx"));
const HowToCompareTrailers = lazy(() => import("./pages/blog/HowToCompareTrailers.tsx"));
const NonCdlVsCdl40Flatbed = lazy(() => import("./pages/blog/NonCdlVsCdl40Flatbed.tsx"));
const TrailerMaintenanceTips = lazy(() => import("./pages/blog/TrailerMaintenanceTips.tsx"));
const MroviTireGuide = lazy(() => import("./pages/blog/Mrovi235_85R16TireGuide.tsx"));
const NonCdlHotshotTrailerWeight = lazy(() => import("./pages/blog/NonCdlHotshotTrailerWeight.tsx"));
const MroviTrailerPartsNortheast = lazy(() => import("./pages/blog/MroviTrailerPartsNortheast.tsx"));
const RentToOwnVsFinancing = lazy(() => import("./pages/blog/RentToOwnVsFinancing.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage.tsx"));
const ContactPage = lazy(() => import("./pages/ContactPage.tsx"));
// New priority city pages — Fort Worth / Tulsa / Shreveport / Little Rock / Waco / Austin RO / San Antonio RO

// Internal Super Admin dashboard (noindex, role-gated)
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout.tsx"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin.tsx"));
const AdminResetPassword = lazy(() => import("./pages/admin/AdminResetPassword.tsx"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard.tsx"));
const AdminLiveTraffic = lazy(() => import("./pages/admin/AdminLiveTraffic.tsx"));
const AdminVisitors = lazy(() => import("./pages/admin/AdminVisitors.tsx"));
const AdminMarkets = lazy(() => import("./pages/admin/AdminMarkets.tsx"));
const AdminEngagement = lazy(() => import("./pages/admin/AdminEngagement.tsx"));
const AdminContacts = lazy(() => import("./pages/admin/AdminContacts.tsx"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads.tsx"));
const AdminSeo = lazy(() => import("./pages/admin/AdminSeo.tsx"));
const AdminSeoValidation = lazy(() => import("./pages/admin/AdminSeoValidation.tsx"));
const AdminTrafficSources = lazy(() => import("./pages/admin/AdminTrafficSources.tsx"));
const AdminContent = lazy(() => import("./pages/admin/AdminContent.tsx"));
const AdminTrailers = lazy(() => import("./pages/admin/AdminTrailers.tsx"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog.tsx"));
const AdminLocations = lazy(() => import("./pages/admin/AdminLocations.tsx"));
const AdminGeoIntel = lazy(() => import("./pages/admin/AdminGeoIntel.tsx"));
const AdminCampaigns = lazy(() => import("./pages/admin/AdminCampaigns.tsx"));
const AdminClicks = lazy(() => import("./pages/admin/AdminClicks.tsx"));
const AdminFinancing = lazy(() => import("./pages/admin/AdminFinancing.tsx"));
const TrailerPayloadCalculatorPage = lazy(() => import("./pages/TrailerPayloadCalculatorPage.tsx"));
const AdminSeoOpportunities = lazy(() => import("./pages/admin/AdminSeoOpportunities.tsx"));
const AdminFunnel = lazy(() => import("./pages/admin/AdminFunnel.tsx"));
const AdminHealth = lazy(() => import("./pages/admin/AdminHealth.tsx"));
const AdminOpportunities = lazy(() => import("./pages/admin/AdminOpportunities.tsx"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports.tsx"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings.tsx"));

// Competitor Intelligence
const CompetitorLayout = lazy(() => import("./pages/admin/competitors/CompetitorLayout.tsx"));
const CompetitorOverview = lazy(() => import("./pages/admin/competitors/CompetitorOverview.tsx"));
const CompetitorManager = lazy(() => import("./pages/admin/competitors/CompetitorManager.tsx"));
const CompetitorDomainScanner = lazy(
  () => import("./pages/admin/competitors/CompetitorDomainScanner.tsx"),
);
const CompetitorPageComparison = lazy(
  () => import("./pages/admin/competitors/CompetitorPageComparison.tsx"),
);
const CompetitorMeta = lazy(() => import("./pages/admin/competitors/CompetitorMeta.tsx"));
const CompetitorKeywordGap = lazy(
  () => import("./pages/admin/competitors/CompetitorKeywordGap.tsx"),
);
const CompetitorRankings = lazy(() => import("./pages/admin/competitors/CompetitorRankings.tsx"));
const CompetitorSerp = lazy(() => import("./pages/admin/competitors/CompetitorSerp.tsx"));
const CompetitorContentGap = lazy(
  () => import("./pages/admin/competitors/CompetitorContentGap.tsx"),
);
const CompetitorTopPages = lazy(() => import("./pages/admin/competitors/CompetitorTopPages.tsx"));
const CompetitorLocal = lazy(() => import("./pages/admin/competitors/CompetitorLocal.tsx"));
const CompetitorLocationGaps = lazy(
  () => import("./pages/admin/competitors/CompetitorLocationGaps.tsx"),
);
const CompetitorBacklinks = lazy(() => import("./pages/admin/competitors/CompetitorBacklinks.tsx"));
const CompetitorBacklinkGap = lazy(
  () => import("./pages/admin/competitors/CompetitorBacklinkGap.tsx"),
);
const CompetitorSchema = lazy(() => import("./pages/admin/competitors/CompetitorSchema.tsx"));
const CompetitorInternalLinks = lazy(
  () => import("./pages/admin/competitors/CompetitorInternalLinks.tsx"),
);
const CompetitorBlog = lazy(() => import("./pages/admin/competitors/CompetitorBlog.tsx"));
const CompetitorChanges = lazy(() => import("./pages/admin/competitors/CompetitorChanges.tsx"));
const CompetitorOpportunities = lazy(
  () => import("./pages/admin/competitors/CompetitorOpportunities.tsx"),
);
const CompetitorReports = lazy(() => import("./pages/admin/competitors/CompetitorReports.tsx"));

const queryClient = new QueryClient();

const RouteSeo = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    applyRouteSeo(pathname);
    if (!pathname.startsWith("/admin")) {
      void trackPageView(pathname);
      startEngagementTracking();
    }
  }, [pathname]);
  return null;
};

/** Public site chrome — hidden on internal admin routes. */
const SiteChrome = ({ position }: { position: "header" | "footer" }) => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  if (position === "header") return <SiteHeader />;
  return (
    <>
      <FooterCTA />
      <SiteFooter />
      <CookieConsent />
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <RouteSeo />
        <SiteChrome position="header" />
        <main>
          <Suspense fallback={<div className="min-h-[60vh]" />}>
          <Routes>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="live" element={<AdminLiveTraffic />} />
              <Route path="visitors" element={<AdminVisitors />} />
              <Route path="markets" element={<AdminMarkets />} />
              <Route path="engagement" element={<AdminEngagement />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="contacts" element={<AdminContacts />} />
              <Route path="seo" element={<AdminSeo />} />
              <Route path="seo-validation" element={<AdminSeoValidation />} />
              <Route path="seo-opportunities" element={<AdminSeoOpportunities />} />
              <Route path="sources" element={<AdminTrafficSources />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="trailers" element={<AdminTrailers />} />
              <Route path="blog" element={<AdminBlog />} />
              <Route path="locations" element={<AdminLocations />} />
              <Route path="geo-intelligence" element={<AdminGeoIntel />} />
              <Route path="campaigns" element={<AdminCampaigns />} />
              <Route path="clicks" element={<AdminClicks />} />
              <Route path="financing" element={<AdminFinancing />} />
              <Route path="funnel" element={<AdminFunnel />} />
              <Route path="health" element={<AdminHealth />} />
              <Route path="opportunities" element={<AdminOpportunities />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="competitor-intel" element={<CompetitorLayout />}>
                <Route index element={<CompetitorOverview />} />
                <Route path="competitors" element={<CompetitorManager />} />
                <Route path="scanner" element={<CompetitorDomainScanner />} />
                <Route path="page-comparison" element={<CompetitorPageComparison />} />
                <Route path="meta" element={<CompetitorMeta />} />
                <Route path="keyword-gap" element={<CompetitorKeywordGap />} />
                <Route path="rankings" element={<CompetitorRankings />} />
                <Route path="serp" element={<CompetitorSerp />} />
                <Route path="content-gap" element={<CompetitorContentGap />} />
                <Route path="top-pages" element={<CompetitorTopPages />} />
                <Route path="local" element={<CompetitorLocal />} />
                <Route path="location-gaps" element={<CompetitorLocationGaps />} />
                <Route path="backlinks" element={<CompetitorBacklinks />} />
                <Route path="backlink-gap" element={<CompetitorBacklinkGap />} />
                <Route path="schema" element={<CompetitorSchema />} />
                <Route path="internal-links" element={<CompetitorInternalLinks />} />
                <Route path="blog" element={<CompetitorBlog />} />
                <Route path="changes" element={<CompetitorChanges />} />
                <Route path="opportunities" element={<CompetitorOpportunities />} />
                <Route path="reports" element={<CompetitorReports />} />
              </Route>
            </Route>
            <Route path="/" element={<Index />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/about-juan" element={<Navigate to="/about" replace />} />
            <Route path="/trailer-guides" element={<TrailerGuidesHubPage />} />
            <Route path="/trailer-guides/:hub" element={<EducationHubPage />} />
            <Route path="/towing-cdl" element={<EducationHubPage hubSlug="towing-cdl" />} />
            <Route path="/maintenance-ownership" element={<EducationHubPage hubSlug="maintenance-ownership" />} />
            <Route path="/trailer-payload-calculator" element={<TrailerPayloadCalculatorPage />} />
            <Route path="/dump-trailers" element={<DumpTrailersPage />} />
            <Route path="/roll-off-trailers" element={<RollOffTrailersPage />} />
            <Route path="/equipment-trailers" element={<Navigate to="/lowboy-trailers" replace />} />
            <Route path="/car-haulers" element={<CarHaulersPage />} />
            <Route path="/flatbed-trailers" element={<FlatbedTrailersPage />} />
            <Route path="/lowboy-trailers" element={<LowboyTrailersPage />} />
            <Route path="/tilt-trailers" element={<TiltTrailersPage />} />
            <Route path="/gravity-tilt-trailers" element={<GravityTiltTrailersPage />} />
            <Route path="/power-tilt-trailers" element={<PowerTiltTrailersPage />} />
            <Route path="/trailer-types" element={<Navigate to="/trailer-guides" replace />} />
            <Route path="/trailer-financing" element={<FinancingPage />} />
            {/* Legacy aliases — permanent redirects to preserve SEO equity */}
            <Route path="/financing" element={<Navigate to="/trailer-financing" replace />} />
            
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/blog" element={<BlogPage />} />
            
            <Route path="/build-your-flatbed" element={<FlatbedBuilderPage />} />
            <Route path="/build-your-roll-off" element={<RollOffBuilderPage />} />
            <Route path="/build-your-roll-off-trailer" element={<Navigate to="/build-your-roll-off" replace />} />
            <Route path="/build-your-gravity-tilt" element={<GravityTiltBuilderPage />} />
            <Route path="/build-your-power-tilt" element={<PowerTiltBuilderPage />} />
            <Route path="/build-your-dump-trailer" element={<DumpTrailerBuilderPage />} />
            <Route path="/build-your-car-hauler" element={<CarHaulerBuilderPage />} />
            <Route path="/build-your-equipment-trailer" element={<EquipmentTrailerBuilderPage />} />
            {/* Lowboy and Equipment Trailers are the same product — share the builder */}
            <Route path="/build-your-lowboy-trailer" element={<EquipmentTrailerBuilderPage />} />
            <Route path="/dump-trailer-buyers-guide" element={<DumpTrailerBuyersGuide />} />
            <Route path="/trailer-financing-buyers-guide" element={<TrailerFinancingBuyersGuide />} />
            {/* Spanish-language education pages (es-US) */}
            <Route path="/es/licencia-cdl-remolques" element={<LicenciaCdlRemolquesPage />} />
            <Route path="/es/gvwr-capacidad-de-carga" element={<GvwrCapacidadDeCargaPage />} />
            <Route path="/es/remolques-de-volteo" element={<RemolquesDeVolteoPage />} />
            <Route path="/es/remolque-volteo-14k-vs-16k" element={<RemolqueVolteo14kVs16kPage />} />
            <Route path="/es/mantenimiento-de-remolques" element={<MantenimientoDeRemolquesPage />} />
            <Route path="/es/costos-de-tener-un-remolque" element={<CostosDeTenerUnRemolquePage />} />
            <Route path="/blog/dump-trailer-guide" element={<Navigate to="/dump-trailer-buyers-guide" replace />} />
            <Route path="/blog/14k-vs-16k-dump-trailer" element={<DumpTrailer14kVs16kPage />} />
            <Route path="/blog/how-to-choose-a-dump-trailer" element={<Navigate to="/dump-trailer-buyers-guide" replace />} />
            <Route path="/blog/roll-off-vs-dump-trailer" element={<DumpVsRollOff />} />
            <Route path="/blog/dump-trailer-vs-roll-off-trailer" element={<Navigate to="/blog/roll-off-vs-dump-trailer" replace />} />
            <Route path="/blog/gravity-tilt-vs-power-tilt" element={<GravityVsPowerTilt />} />
            <Route path="/blog/best-dump-trailer-for-contractors" element={<BestDumpTrailerContractors />} />
            <Route path="/blog/what-is-a-lowboy-trailer" element={<WhatIsALowboyTrailer />} />
            <Route path="/blog/lowboy-trailer-deck-width" element={<LowboyTrailerDeckWidth />} />
            <Route path="/blog/lowboy-trailer-axle-capacity" element={<LowboyTrailerAxleCapacity />} />
            <Route path="/blog/lowboy-trailer-rails-vs-open-deck" element={<LowboyTrailerRailsVsOpenDeck />} />
            <Route path="/blog/gooseneck-vs-bumper-pull-lowboy" element={<GooseneckVsBumperPullLowboy />} />
            <Route path="/blog/best-trailer-for-skid-steer" element={<BestTrailerForSkidSteer />} />
            <Route path="/blog/best-trailer-for-mini-excavator" element={<BestTrailerForMiniExcavator />} />
            <Route path="/blog/lowboy-trailer-guides" element={<LowboyGuidesHub />} />
            <Route path="/blog/lowboy-trailers-explained" element={<Navigate to="/blog/what-is-a-lowboy-trailer" replace />} />
            <Route path="/blog/lowboy-vs-equipment" element={<LowboyVsEquipment />} />
            <Route path="/blog/lowboy-trailer-size" element={<LowboyTrailerSize />} />
            <Route path="/blog/lowboy-weight-capacity" element={<LowboyWeightCapacity />} />
            <Route path="/blog/lowboy-ramps-vs-tilt" element={<LowboyRampsVsTilt />} />
            <Route path="/blog/roll-off-trailers-explained" element={<RollOffTrailersExplained />} />
            <Route path="/blog/roll-off-rail-width" element={<RollOffRailWidth />} />
            <Route path="/blog/roll-off-cdl" element={<RollOffCDL />} />
            <Route path="/blog/roll-off-derating" element={<RollOffDerating />} />
            <Route path="/blog/roll-off-size" element={<RollOffSize />} />
            <Route path="/blog/roll-off-residential-vs-commercial" element={<RollOffResidentialVsCommercial />} />
            <Route path="/podcast" element={<PodcastPage />} />
            {/* Dynamic route for data-driven blog articles */}
            <Route path="/blog/how-to-compare-trailers" element={<HowToCompareTrailers />} />
            <Route path="/blog/non-cdl-vs-cdl-40-flatbed" element={<NonCdlVsCdl40Flatbed />} />
            <Route path="/blog/trailer-maintenance-tips" element={<TrailerMaintenanceTips />} />
            <Route path="/blog/rent-to-own-vs-trailer-financing" element={<RentToOwnVsFinancing />} />
            <Route path="/blog/235-85r16-14-ply-trailer-tire-guide" element={<MroviTireGuide />} />
            <Route path="/blog/mrovi-trailer-parts-northeast-trailer-parts-supplier" element={<MroviTrailerPartsNortheast />} />
            <Route path="/blog/non-cdl-hotshot-trailer-weight-guide" element={<NonCdlHotshotTrailerWeight />} />
            <Route
              path="/blog/non-cdl-hotshot-trailer-weight-payload"
              element={<Navigate to="/blog/non-cdl-hotshot-trailer-weight-guide" replace />}
            />
            <Route path="/blog/:slug" element={<DynamicBlogArticle />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<PrivacyPolicyPage />} />
            <Route path="/cookie-policy" element={<PrivacyPolicyPage />} />
            <Route path="/dump-trailers-houston-tx" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/dump-trailers-dallas-tx" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/trailer-financing-texas" element={<Navigate to="/trailer-financing" replace />} />
            <Route path="/texas-trailer-pages" element={<Navigate to="/trailer-guides" replace />} />
            <Route path="/roll-off-trailers-houston-tx" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="/dump-trailers-austin-tx" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/dump-trailers-san-antonio-tx" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/roll-off-trailers-dallas-tx" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="/dump-trailers-phoenix-az" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/dump-trailers-atlanta-ga" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/dump-trailers-charlotte-nc" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/dump-trailers-nashville-tn" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/dump-trailers-tampa-fl" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/dump-trailers-denver-co" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/dump-trailers-las-vegas-nv" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/dump-trailers-orlando-fl" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/dump-trailers-kansas-city-mo" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/dump-trailers-oklahoma-city-ok" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/roll-off-trailers-phoenix-az" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="/roll-off-trailers-atlanta-ga" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="/roll-off-trailers-charlotte-nc" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="/roll-off-trailers-nashville-tn" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="/roll-off-trailers-tampa-fl" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="/roll-off-trailers-denver-co" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="/roll-off-trailers-las-vegas-nv" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="/roll-off-trailers-orlando-fl" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="/roll-off-trailers-kansas-city-mo" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="/roll-off-trailers-oklahoma-city-ok" element={<Navigate to="/roll-off-trailers" replace />} />
            {/* New priority city pages */}
            <Route path="/dump-trailers-fort-worth-tx" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/dump-trailers-tulsa-ok" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/dump-trailers-shreveport-la" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/dump-trailers-little-rock-ar" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/dump-trailers-waco-tx" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/roll-off-trailers-fort-worth-tx" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="/roll-off-trailers-austin-tx" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="/roll-off-trailers-san-antonio-tx" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="/roll-off-trailers-tulsa-ok" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="/roll-off-trailers-shreveport-la" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="/roll-off-trailers-little-rock-ar" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="/roll-off-trailers-waco-tx" element={<Navigate to="/roll-off-trailers" replace />} />
            {/* Top Texas markets — June 2026 batch */}
            <Route path="/dump-trailers-tyler-tx" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/roll-off-trailers-tyler-tx" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="/dump-trailers-lubbock-tx" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/roll-off-trailers-lubbock-tx" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="/dump-trailers-corpus-christi-tx" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/roll-off-trailers-corpus-christi-tx" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="/dump-trailers-mcallen-tx" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/roll-off-trailers-mcallen-tx" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="/dump-trailers-bryan-college-station-tx" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/roll-off-trailers-bryan-college-station-tx" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="/dump-trailers-beaumont-tx" element={<Navigate to="/dump-trailers" replace />} />
            <Route path="/roll-off-trailers-beaumont-tx" element={<Navigate to="/roll-off-trailers" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </main>
        <SiteChrome position="footer" />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
